"use client";

import { forwardRef, ButtonHTMLAttributes, ReactNode } from "react";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "icon";
type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon;
  children?: ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    "bg-primary text-primary-foreground font-semibold border border-transparent " +
    "hover:bg-primary-light hover:[box-shadow:var(--halo-strong)] " +
    "active:bg-primary-dark active:[box-shadow:0_0_0_1px_var(--color-brand-glow-30)] active:[filter:brightness(0.92)]",
  secondary:
    "bg-brand-glow-08 border border-brand-glow-20 text-brand-light " +
    "hover:bg-brand-glow-12 hover:border-brand-glow-30 " +
    "active:[filter:brightness(0.9)]",
  ghost:
    "bg-transparent text-muted-foreground border border-transparent " +
    "hover:bg-card-hover hover:text-brand-light hover:border-border",
  danger:
    "bg-error text-primary-foreground font-semibold border border-transparent " +
    "hover:bg-error-light hover:[box-shadow:var(--halo-danger)] active:[filter:brightness(0.92)]",
  icon:
    "bg-transparent text-muted-foreground border border-transparent p-[9px] rounded-[10px] min-w-11 lg:min-w-0 " +
    "hover:bg-card-hover hover:text-brand-light hover:border-border",
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-[13px] gap-1.5 rounded-xl",
  md: "px-4 py-[9px] text-sm gap-2 rounded-xl",
  lg: "px-[22px] py-3 text-[15px] gap-2.5 rounded-xl",
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      isLoading = false,
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      children,
      className = "",
      disabled,
      "aria-label": ariaLabelProp,
      ...props
    },
    ref,
  ) => {
    const isIconOnly = variant === "icon";
    const ariaLabel = ariaLabelProp ?? (isIconOnly ? "Action" : undefined);

    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        aria-label={ariaLabel}
        data-variant={variant}
        data-size={isIconOnly ? undefined : size}
        className={cn(
          "inline-flex items-center justify-center font-medium tracking-tight whitespace-nowrap",
          "min-h-11 lg:min-h-0",
          "transition duration-[220ms] ease-[cubic-bezier(0.2,0,0.1,1)]",
          "focus-ring",
          "disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none",
          variantStyles[variant],
          !isIconOnly && sizeStyles[size],
          className,
        )}
        {...props}
      >
        {isLoading ? (
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          <>
            {LeftIcon && <LeftIcon className="w-4 h-4 shrink-0" />}
            {children}
            {RightIcon && <RightIcon className="w-4 h-4 shrink-0" />}
          </>
        )}
      </button>
    );
  },
);

Button.displayName = "Button";

export { Button, type ButtonProps, type ButtonVariant, type ButtonSize };
